import type { CodingAnswerOption } from '@seen/core/codings'
import { Button, cn } from '@seen/ui'
import {
  IconCheck,
  IconChevronDown,
  IconPlus,
  IconX,
} from '@tabler/icons-react'
import type { ComponentProps, JSX } from 'react'
import { Combobox } from '#src/components/core/combobox/Combobox.tsx'
import { Input } from '#src/components/core/input/Input.tsx'
import { DatePicker } from '#src/components/input/DatePicker.tsx'
import {
  type BuilderGroup,
  type BuilderRow,
  type BuilderState,
  type ConditionSource,
  operatorsFor,
  type RowOperator,
} from '#src/components/rich-text-editor/conditional-display/conditionBuilderModel.ts'
import { answerChipClass } from '#src/components/rich-text-editor/ObservationNode/observationChip.ts'
import { ValueSetAnswerPicker } from '#src/components/rich-text-editor/ObservationNode/ValueSetAnswerPicker.tsx'
import { useElementBindingValueSet } from '#src/hooks/useElementBindingValueSet.ts'

export interface ConditionBuilderProps {
  value: BuilderState
  onChange: (value: BuilderState) => void
  sources: readonly ConditionSource[]
}

const OPERATOR_LABELS: Record<RowOperator, string> = {
  '=': 'is',
  '!=': 'is not',
  '<': '<',
  '<=': '≤',
  '>': '>',
  '>=': '≥',
  in: 'is one of',
  answered: 'is answered',
  'not answered': 'is not answered',
}

function newRow(sources: readonly ConditionSource[]): BuilderRow {
  return { type: 'row', source: sources[0]?.key ?? '', op: '=', values: [] }
}

// The condition as rows: what to read, how to compare it, and with what;
// joined by "all" or "any", with one level of groups inside.
export function ConditionBuilder({
  value,
  onChange,
  sources,
}: ConditionBuilderProps): JSX.Element {
  const setItem = (index: number, item: BuilderRow | BuilderGroup): void =>
    onChange({
      ...value,
      items: value.items.map((current, at) => (at === index ? item : current)),
    })
  const removeItem = (index: number): void =>
    onChange({ ...value, items: value.items.filter((_, at) => at !== index) })

  return (
    <div className="flex flex-col gap-2 text-xs">
      <JoinSentence
        lead="Show when"
        join={value.join}
        onChange={(join) => onChange({ ...value, join })}
      />
      {value.items.map((item, index) =>
        item.type === 'row' ? (
          <RowEditor
            // biome-ignore lint/suspicious/noArrayIndexKey: rows have no identity of their own
            key={index}
            row={item}
            sources={sources}
            onChange={(row) => setItem(index, row)}
            onRemove={() => removeItem(index)}
          />
        ) : (
          <div
            // biome-ignore lint/suspicious/noArrayIndexKey: groups have no identity of their own
            key={index}
            className="ml-1 flex flex-col gap-1.5 border-l pl-4"
          >
            <div className="flex items-center justify-between">
              <JoinSentence
                lead="Where"
                join={item.join}
                onChange={(join) => setItem(index, { ...item, join })}
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label="Remove group"
                className="size-6"
                onClick={() => removeItem(index)}
              >
                <IconX className="size-3.5" />
              </Button>
            </div>
            {item.rows.map((row, rowIndex) => (
              <RowEditor
                // biome-ignore lint/suspicious/noArrayIndexKey: rows have no identity of their own
                key={rowIndex}
                row={row}
                sources={sources}
                onChange={(next) =>
                  setItem(index, {
                    ...item,
                    rows: item.rows.map((current, at) =>
                      at === rowIndex ? next : current
                    ),
                  })
                }
                onRemove={() =>
                  setItem(index, {
                    ...item,
                    rows: item.rows.filter((_, at) => at !== rowIndex),
                  })
                }
              />
            ))}
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="self-start"
              onClick={() =>
                setItem(index, {
                  ...item,
                  rows: [...item.rows, newRow(sources)],
                })
              }
            >
              <IconPlus className="size-4" /> Add condition
            </Button>
          </div>
        )
      )}
      <div className="flex gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() =>
            onChange({ ...value, items: [...value.items, newRow(sources)] })
          }
        >
          <IconPlus className="size-4" /> Add condition
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() =>
            onChange({
              ...value,
              items: [
                ...value.items,
                {
                  type: 'group',
                  join: value.join === 'all' ? 'any' : 'all',
                  rows: [newRow(sources)],
                },
              ],
            })
          }
        >
          <IconPlus className="size-4" /> Add group
        </Button>
      </div>
    </div>
  )
}

// "When [all] of these are true", the join as a small select.
function JoinSentence({
  lead,
  join,
  onChange,
}: {
  lead: string
  join: 'all' | 'any'
  onChange: (join: 'all' | 'any') => void
}): JSX.Element {
  return (
    <span className="flex items-center gap-1.5 text-muted-foreground">
      {lead}
      <select
        aria-label="Join"
        value={join}
        onChange={(event) => onChange(event.target.value as 'all' | 'any')}
        className={SELECT_CLASS}
      >
        <option value="all">all</option>
        <option value="any">any</option>
      </select>
      of these are true
    </span>
  )
}

const SELECT_CLASS =
  'h-7 rounded-md border bg-background px-1 text-foreground text-xs focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-zinc-950'

function RowEditor({
  row,
  sources,
  onChange,
  onRemove,
}: {
  row: BuilderRow
  sources: readonly ConditionSource[]
  onChange: (row: BuilderRow) => void
  onRemove: () => void
}): JSX.Element {
  const source = sources.find((candidate) => candidate.key === row.source)
  const operators = source ? operatorsFor(source.kind) : []
  const options = (group: ConditionSource['group']) =>
    sources
      .filter((candidate) => candidate.group === group)
      .map((candidate) => ({
        value: candidate.key,
        label: (
          <CheckedLabel
            label={candidate.label}
            checked={candidate.key === row.source}
          />
        ),
        keywords: [candidate.label, candidate.key],
      }))

  const hasValue =
    !!source && row.op !== 'answered' && row.op !== 'not answered'
  const coded = hasValue && source?.kind === 'coding'
  const chips = coded && row.op === 'in'

  return (
    <div
      className="grid grid-cols-[minmax(0,1.4fr)_auto_minmax(0,1fr)_auto] items-center gap-1.5"
      data-condition-row=""
    >
      <Combobox
        options={options('Context')}
        suggestedOptions={options('In this note')}
        suggestedLabel="In this note"
        fuseThreshold={0.3}
        onSelect={(key) => {
          const next = sources.find((candidate) => candidate.key === key)
          const keep = next && operatorsFor(next.kind).includes(row.op)
          onChange({
            type: 'row',
            source: key ?? '',
            op: keep ? row.op : '=',
            values: [],
          })
        }}
      >
        <Button
          type="button"
          variant="outline"
          size="sm"
          aria-label="Source"
          title={source?.label}
          className="h-7 w-full min-w-0 justify-between px-2 font-normal"
        >
          <span className="truncate">{source?.label ?? 'Choose…'}</span>
          <IconChevronDown className="size-3 shrink-0" />
        </Button>
      </Combobox>
      <select
        aria-label="Operator"
        value={row.op}
        onChange={(event) =>
          onChange({ ...row, op: event.target.value as RowOperator })
        }
        className={SELECT_CLASS}
      >
        {operators.map((operator) => (
          <option key={operator} value={operator}>
            {OPERATOR_LABELS[operator]}
          </option>
        ))}
      </select>
      <span className="min-w-0">
        {coded && source && !chips && (
          <CodedValue source={source} row={row} onChange={onChange} />
        )}
        {hasValue && source && !coded && (
          <ValueControl
            source={source}
            multiple={row.op === 'in'}
            values={row.values}
            onChange={(values) => onChange({ ...row, values })}
          />
        )}
      </span>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        aria-label="Remove condition"
        className="size-6"
        onClick={onRemove}
      >
        <IconX className="size-3.5" />
      </Button>
      {chips && source && (
        <span className="col-span-2 col-start-3">
          {source.answerOptions?.length ? (
            <AnswerChips
              source={source}
              values={row.values}
              onChange={(values) => onChange({ ...row, values })}
            />
          ) : (
            <PickedChips source={source} row={row} onChange={onChange} />
          )}
        </span>
      )}
    </div>
  )
}

// "Is one of": the answers as toggles, on their own line under the value.
function AnswerChips({
  source,
  values,
  onChange,
}: {
  source: ConditionSource
  values: string[]
  onChange: (values: string[]) => void
}): JSX.Element {
  return (
    <span className="flex flex-wrap gap-1">
      {(source.answerOptions ?? []).map((option) => (
        <button
          key={option.code}
          type="button"
          aria-pressed={values.includes(option.code)}
          onClick={() =>
            onChange(
              values.includes(option.code)
                ? values.filter((value) => value !== option.code)
                : [...values, option.code]
            )
          }
          className={answerChipClass(values.includes(option.code))}
        >
          {option.display}
        </button>
      ))}
    </span>
  )
}

function ValueControl({
  source,
  multiple,
  values,
  onChange,
}: {
  source: ConditionSource
  multiple: boolean
  values: string[]
  onChange: (values: string[]) => void
}): JSX.Element {
  const toggle = (code: string): void => {
    if (!multiple) {
      onChange([code])
      return
    }
    onChange(
      values.includes(code)
        ? values.filter((value) => value !== code)
        : [...values, code]
    )
  }
  switch (source.kind) {
    case 'number':
      return (
        <span className="flex items-center gap-1">
          <Input
            aria-label="Value"
            type="number"
            value={values[0] ?? ''}
            onChange={(event) => onChange([event.target.value])}
            className="h-7 w-full min-w-0"
          />
          {source.unit && (
            <span className="text-xs text-muted-foreground">{source.unit}</span>
          )}
        </span>
      )
    case 'date':
      return (
        <DatePicker
          value={values[0] ?? null}
          onChange={(value) => onChange(value ? [value] : [])}
          placeholder="Date"
          className="h-7 w-full"
        />
      )
    case 'boolean':
      return (
        <span className="inline-flex gap-1">
          {(['yes', 'no'] as const).map((option) => (
            <button
              key={option}
              type="button"
              aria-pressed={values.includes(option)}
              onClick={() => toggle(option)}
              className={answerChipClass(values.includes(option))}
            >
              {option === 'yes' ? 'Yes' : 'No'}
            </button>
          ))}
        </span>
      )
    case 'coding':
      // Coded rows are edited by CodedValue / the answer chips.
      return <NoAnswerSet />
    case 'string':
      return (
        <Input
          aria-label="Value"
          value={values[0] ?? ''}
          onChange={(event) => onChange([event.target.value])}
          className="h-7 w-full"
        />
      )
  }
}

function NoAnswerSet(): JSX.Element {
  return <DisabledValue text="No answer set" />
}

function DisabledValue({ text }: { text: string }): JSX.Element {
  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      aria-label="Value"
      disabled
      className="h-7 w-full min-w-0 justify-start px-2 font-normal"
    >
      <span className="truncate">{text}</span>
    </Button>
  )
}

function CheckedLabel({
  label,
  checked,
}: {
  label: string
  checked: boolean
}): JSX.Element {
  return (
    <span className="flex w-full items-center justify-between gap-2">
      <span className={cn(checked && 'font-medium')}>{label}</span>
      {checked && (
        <IconCheck aria-label="Selected" className="size-3.5 shrink-0" />
      )}
    </span>
  )
}

// Spreads what a popover trigger passes it (ref, handlers) onto the button.
function ValueTrigger({
  text,
  ...props
}: { text: string | undefined } & ComponentProps<typeof Button>): JSX.Element {
  return (
    <Button
      {...props}
      type="button"
      variant="outline"
      size="sm"
      aria-label="Value"
      title={text}
      className="h-7 w-full min-w-0 justify-between px-2 font-normal"
    >
      <span className="truncate">{text ?? 'Choose…'}</span>
      <IconChevronDown className="size-3 shrink-0" />
    </Button>
  )
}

// The value set a coded source's answers come from: its own, else the one
// its FHIR element is bound to.
function useAnswerValueSet(source: ConditionSource): {
  valueSet: string | undefined
  loading: boolean
} {
  const carried = Boolean(source.answerOptions?.length || source.answerValueSet)
  const bound = useElementBindingValueSet(
    carried ? undefined : source.elementDefinition
  )
  return source.answerValueSet
    ? { valueSet: source.answerValueSet, loading: false }
    : bound
}

// How a picked code reads: its display from the inline answers or from
// when it was picked, else the code.
function answerText(
  source: ConditionSource,
  row: BuilderRow,
  code: string
): string {
  return (
    source.answerOptions?.find((option) => option.code === code)?.display ??
    row.picked?.[code]?.display ??
    code
  )
}

function withPick(
  row: BuilderRow,
  answer: CodingAnswerOption
): BuilderRow['picked'] {
  const pick: NonNullable<BuilderRow['picked']>[string] = {}
  if (answer.system) {
    pick.system = answer.system
  }
  if (answer.display) {
    pick.display = answer.display
  }
  return { ...row.picked, [answer.code]: pick }
}

// One coded answer, from the node's own answers: inline, or searched in the
// value set it is bound to. Never typed: a code must come from the set.
function CodedValue({
  source,
  row,
  onChange,
}: {
  source: ConditionSource
  row: BuilderRow
  onChange: (row: BuilderRow) => void
}): JSX.Element {
  const { valueSet, loading } = useAnswerValueSet(source)
  const code = row.values[0]
  const text = code ? answerText(source, row, code) : undefined
  if (source.answerOptions?.length) {
    return (
      <Combobox
        options={source.answerOptions.map((option) => ({
          value: option.code,
          label: (
            <CheckedLabel
              label={option.display ?? option.code}
              checked={option.code === code}
            />
          ),
          keywords: [option.display ?? '', option.code],
        }))}
        onSelect={(picked) =>
          onChange({ ...row, values: picked ? [picked] : [] })
        }
      >
        <ValueTrigger text={text} />
      </Combobox>
    )
  }
  if (loading) {
    return <DisabledValue text="Loading answers…" />
  }
  if (valueSet) {
    return (
      <ValueSetAnswerPicker
        answerValueSet={valueSet}
        onSelect={(answer) =>
          onChange({
            ...row,
            values: [answer.code],
            picked: withPick(row, answer),
          })
        }
      >
        <ValueTrigger text={text} />
      </ValueSetAnswerPicker>
    )
  }
  return <NoAnswerSet />
}

// "Is one of" over a value set: the chosen answers as removable chips and
// a search to add another.
function PickedChips({
  source,
  row,
  onChange,
}: {
  source: ConditionSource
  row: BuilderRow
  onChange: (row: BuilderRow) => void
}): JSX.Element {
  const { valueSet, loading } = useAnswerValueSet(source)
  if (loading) {
    return <DisabledValue text="Loading answers…" />
  }
  if (!valueSet) {
    return <NoAnswerSet />
  }
  return (
    <span className="flex flex-wrap items-center gap-1">
      {row.values.map((code) => (
        <span
          key={code}
          data-picked-answer={code}
          className="inline-flex items-center gap-0.5 rounded-md border px-1.5 text-xs leading-5"
        >
          {answerText(source, row, code)}
          <button
            type="button"
            aria-label={`Remove ${answerText(source, row, code)}`}
            onClick={() =>
              onChange({
                ...row,
                values: row.values.filter((value) => value !== code),
              })
            }
            className="text-muted-foreground hover:text-foreground"
          >
            <IconX className="size-3" />
          </button>
        </span>
      ))}
      <ValueSetAnswerPicker
        answerValueSet={valueSet}
        onSelect={(answer) =>
          row.values.includes(answer.code)
            ? undefined
            : onChange({
                ...row,
                values: [...row.values, answer.code],
                picked: withPick(row, answer),
              })
        }
      >
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-6 px-1.5 text-xs font-normal"
        >
          <IconPlus className="size-3" /> Add answer…
        </Button>
      </ValueSetAnswerPicker>
    </span>
  )
}
